// Client-side SQL Server backup validation utilities

export interface ClientValidationResult {
  isValid: boolean;
  error?: string;
  fileType: 'bak' | 'zip' | 'unknown';
  validatedFiles?: string[]; // For zip files, list of valid .bak files found
  warnings?: string[];
}

/**
 * Validates if a file extension indicates a SQL Server backup file
 */
export function hasValidSqlServerExtension(filename: string): boolean {
  const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  return ext === '.bak';
}

/**
 * Basic client-side validation for file extension
 */
export function validateSqlServerBackupFile(file: File): ClientValidationResult {
  const hasValidExt = hasValidSqlServerExtension(file.name);
  
  if (!hasValidExt) {
    return {
      isValid: false,
      error: `File "${file.name}" must have a .bak extension for SQL Server backup files`,
      fileType: 'unknown'
    };
  }

  return {
    isValid: true,
    fileType: 'bak'
  };
}

/**
 * Read zip file entries using browser APIs
 */
async function readZipEntries(file: File): Promise<{ entries: string[]; totalSize: number }> {
  // Use JSZip for client-side zip reading
  const JSZip = (await import('jszip')).default;
  
  const arrayBuffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);
  
  const entries: string[] = [];
  let totalSize = 0;
  
  zip.forEach((relativePath, zipEntry) => {
    if (!zipEntry.dir) {
      entries.push(relativePath);
      // JSZip uses _data.uncompressedSize for the uncompressed size
      totalSize += (zipEntry as any)._data?.uncompressedSize || 0;
    }
  });
  
  return { entries, totalSize };
}

/**
 * Sanitizes filename to prevent path traversal attacks
 */
function sanitizeFilename(filename: string): string {
  // Remove path separators and dangerous characters
  return filename.split('/').pop()?.split('\\').pop()
    ?.replace(/[<>:"|?*]/g, '')
    ?.replace(/\.\./g, '')
    ?.trim() || '';
}

/**
 * Client-side validation for zip files containing SQL Server backups
 */
export async function validateSqlServerBackupZip(
  file: File,
  options: {
    maxFiles?: number;
    maxUncompressedSize?: number;
  } = {}
): Promise<ClientValidationResult> {
  const { 
    maxFiles = 100,
    maxUncompressedSize = 50 * 1024 * 1024 * 1024 // 50GB
  } = options;

  try {
    const { entries, totalSize } = await readZipEntries(file);
    
    if (entries.length === 0) {
      return {
        isValid: false,
        error: 'Zip file is empty',
        fileType: 'zip'
      };
    }

    if (entries.length > maxFiles) {
      return {
        isValid: false,
        error: `Zip file contains too many files (${entries.length}). Maximum allowed: ${maxFiles}`,
        fileType: 'zip'
      };
    }

    if (totalSize > maxUncompressedSize) {
      return {
        isValid: false,
        error: `Zip file uncompressed size exceeds maximum allowed size (${Math.round(maxUncompressedSize / 1024 / 1024 / 1024)}GB)`,
        fileType: 'zip'
      };
    }

    const validatedFiles: string[] = [];
    const warnings: string[] = [];

    for (const entryName of entries) {
      const sanitizedName = sanitizeFilename(entryName);
      
      // Security check: prevent zip-slip attacks
      if (entryName !== sanitizedName || entryName.includes('..') || entryName.startsWith('/')) {
        return {
          isValid: false,
          error: `Zip file contains unsafe path: "${entryName}"`,
          fileType: 'zip'
        };
      }

      // Check for nested archives (security risk)
      const ext = entryName.toLowerCase().substring(entryName.lastIndexOf('.'));
      if (['.zip', '.rar', '.7z', '.tar', '.gz'].includes(ext)) {
        return {
          isValid: false,
          error: `Zip file contains nested archive: "${entryName}". Nested archives are not allowed for security reasons.`,
          fileType: 'zip'
        };
      }

      // Check for executable files (security risk)
      if (['.exe', '.bat', '.cmd', '.ps1', '.sh', '.scr', '.com', '.dll'].includes(ext)) {
        return {
          isValid: false,
          error: `Zip file contains executable file: "${entryName}". Executable files are not allowed for security reasons.`,
          fileType: 'zip'
        };
      }

      // Validate that file has .bak extension
      if (!hasValidSqlServerExtension(entryName)) {
        return {
          isValid: false,
          error: `File "${entryName}" in zip archive must have a .bak extension for SQL Server backup files`,
          fileType: 'zip'
        };
      }

      validatedFiles.push(entryName);
    }

    if (validatedFiles.length === 0) {
      return {
        isValid: false,
        error: 'Zip file contains no valid SQL Server backup files',
        fileType: 'zip'
      };
    }

    return {
      isValid: true,
      fileType: 'zip',
      validatedFiles,
      warnings: warnings.length > 0 ? warnings : undefined
    };

  } catch (error) {
    console.error('Failed to validate zip file:', error);
    return {
      isValid: false,
      error: 'Failed to read zip file. File may be corrupted or not a valid zip archive.',
      fileType: 'zip'
    };
  }
}

/**
 * Main client-side validation function
 */
export async function validateUploadedFile(
  file: File,
  options: {
    allowZip?: boolean;
    maxZipFiles?: number;
    maxZipUncompressedSize?: number;
  } = {}
): Promise<ClientValidationResult> {
  const {
    allowZip = true,
    maxZipFiles = 100,
    maxZipUncompressedSize = 50 * 1024 * 1024 * 1024
  } = options;

  const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  
  // Handle zip files
  if (ext === '.zip') {
    if (!allowZip) {
      return {
        isValid: false,
        error: 'Zip files are not allowed',
        fileType: 'zip'
      };
    }
    
    return validateSqlServerBackupZip(file, {
      maxFiles: maxZipFiles,
      maxUncompressedSize: maxZipUncompressedSize
    });
  }
  
  // Handle individual .bak files
  return validateSqlServerBackupFile(file);
}

/**
 * Format validation result for user display
 */
export function formatValidationResult(result: ClientValidationResult): string {
  if (result.isValid) {
    if (result.fileType === 'zip') {
      const fileCount = result.validatedFiles?.length || 0;
      return `✓ Valid zip file containing ${fileCount} SQL Server backup file${fileCount !== 1 ? 's' : ''}`;
    } else {
      return '✓ Valid SQL Server backup file';
    }
  } else {
    return `✗ ${result.error}`;
  }
}